import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";
import { useCourseBasePath } from "@/hooks/useCourseBasePath";
import { useEnrollment } from "@/hooks/useEnrollment";
import {
  ModuleRow,
  type ModuleProgressInfo,
} from "@/components/dashboard/ModuleRow";
import { moduleStatusFromProgress } from "@/components/dashboard/moduleStatusFromProgress";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatLastActivity } from "@/lib/utils";

// Number of quiz slots always rendered once the learner has any quiz activity.
// Fewer than this many answers are left-padded with empty (grey) slots.
const QUIZ_SLOT_COUNT = 10;

interface ParticipantDetailsPanelProps {
  participantEnrollmentId: string;
  colorIndex?: number;
  currentModuleTitle?: string | null;
  /** Average course progress across the group, in percent. */
  groupAveragePercent?: number;
  onClose?: () => void;
}

const trainerPreviewPathForLearner = (
  basePath: string,
  nextLessonId: string | null | undefined,
  nextContentElementId: string | null | undefined
) => {
  if (!nextLessonId) return basePath;
  if (!nextContentElementId) return `${basePath}/${nextLessonId}`;
  return `${basePath}/${nextLessonId}/${nextContentElementId}`;
};

const formatStartedAt = (date: Date): string =>
  new Intl.DateTimeFormat("de-DE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <span className="text-xs font-bold tracking-wide text-muted-foreground uppercase">
    {children}
  </span>
);

const StatTile = ({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}) => (
  <div className="bg-accent rounded-md px-3 py-2 flex flex-col gap-0.5 min-w-0">
    <span className="text-[10px] font-bold tracking-wide text-muted-foreground uppercase truncate">
      {label}
    </span>
    <span className={cn("text-lg font-bold leading-tight", valueClassName)}>
      {value}
    </span>
  </div>
);

const LoadingState = () => (
  <Card className="py-5 gap-4">
    <CardContent className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <Skeleton className="size-12 rounded-full" />
        <div className="flex flex-col gap-2 flex-1">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
      <Skeleton className="h-40 rounded-lg" />
      <Skeleton className="h-10 rounded-lg" />
    </CardContent>
  </Card>
);

export const ParticipantDetailsPanel = ({
  participantEnrollmentId,
  colorIndex,
  currentModuleTitle,
  groupAveragePercent,
  onClose,
}: ParticipantDetailsPanelProps) => {
  const navigate = useNavigate();
  const basePath = useCourseBasePath();
  const {
    data: enrollment,
    error,
    isLoading,
  } = useEnrollment(participantEnrollmentId);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const currentModuleRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enrollment || !currentModuleTitle) return;
    const node = currentModuleRef.current;
    const container = scrollContainerRef.current;
    if (!node || !container) return;
    const top =
      node.offsetTop -
      container.offsetTop -
      container.clientHeight / 2 +
      node.clientHeight / 2;
    container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }, [enrollment, currentModuleTitle, participantEnrollmentId]);

  if (isLoading) return <LoadingState />;
  if (error) {
    return (
      <Card className="py-5">
        <CardContent>
          <p className="text-sm text-destructive">
            Details konnten nicht geladen werden:{" "}
            {error instanceof Error ? error.message : "Unbekannter Fehler"}
          </p>
        </CardContent>
      </Card>
    );
  }
  if (!enrollment?.user || !enrollment.enrolledAt) return null;

  const lastActiveAt = enrollment.lastActiveAt
    ? new Date(enrollment.lastActiveAt)
    : null;
  const startedAt = new Date(enrollment.enrolledAt);
  const lastActivityLabel = lastActiveAt
    ? formatLastActivity(lastActiveAt)
    : null;

  const modules: ModuleProgressInfo[] =
    enrollment.progress?.modules?.map((module) => ({
      title: module.title,
      chapterCount: module.chapters.length,
      progressPercent: module.progressPercent,
      status: moduleStatusFromProgress(module.progressPercent),
    })) ?? [];

  const recentQuizAnswers = enrollment.recentQuizAnswers ?? [];
  const quizAveragePercent = enrollment.quizAveragePercent;
  const hasQuizAverage = quizAveragePercent != null;
  const emptyQuizSlots = Math.max(
    0,
    QUIZ_SLOT_COUNT - recentQuizAnswers.length
  );

  const overallProgressPercent =
    modules.length > 0
      ? Math.round(
          modules.reduce(
            (totalProgress, moduleSummary) =>
              totalProgress + moduleSummary.progressPercent,
            0
          ) / modules.length
        )
      : 0;

  const openCourseAtLearnerPosition = () => {
    navigate(
      trainerPreviewPathForLearner(
        basePath,
        enrollment.progress?.nextLessonId,
        enrollment.progress?.nextContentElementId
      )
    );
  };

  return (
    <Card className="py-3 gap-3 lg:h-full lg:min-h-0 lg:overflow-hidden">
      <CardHeader className="pb-0 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2.5 min-w-0">
            <UserAvatar
              firstName={enrollment.user.firstName ?? ""}
              lastNameInitial={enrollment.user.lastName?.[0]}
              colorIndex={colorIndex ?? 0}
              size="md"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-base font-bold text-primary leading-tight">
                  {enrollment.user.firstName} {enrollment.user.lastName?.[0]}.
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1 leading-snug">
                {lastActivityLabel && <div>{lastActivityLabel}</div>}
                <div>Gestartet am {formatStartedAt(startedAt)}</div>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            {onClose && (
              <Button
                variant="ghost"
                size="icon-sm"
                className="-mt-1 -mr-1"
                onClick={onClose}
                aria-label="Auswahl schließen"
              >
                <X className="size-4" />
              </Button>
            )}
            {recentQuizAnswers.length > 0 && (
              <div className="flex flex-col gap-1.5 items-end">
                <SectionHeading>
                  Letzte {QUIZ_SLOT_COUNT} Quiz-Antworten
                </SectionHeading>
                <div className="flex gap-1">
                  {Array.from({ length: emptyQuizSlots }).map((_, i) => (
                    <span
                      key={`empty-${i}`}
                      className="size-5 rounded-sm bg-muted"
                      aria-hidden
                    />
                  ))}
                  {recentQuizAnswers.map((correct, i) => (
                    <span
                      key={i}
                      title={correct ? "Richtig" : "Falsch"}
                      className={cn(
                        "size-5 rounded-sm flex items-center justify-center",
                        correct
                          ? "bg-success-light text-success-foreground"
                          : "bg-destructive/10 text-destructive"
                      )}
                    >
                      {correct ? (
                        <Check className="size-3" strokeWidth={3} />
                      ) : (
                        <X className="size-3" strokeWidth={3} />
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <div className="px-4 flex flex-col gap-3 shrink-0 pb-5">
        <div className="grid grid-cols-3 gap-2">
          <StatTile
            label="Fortschritt"
            value={`${overallProgressPercent}%`}
            valueClassName="text-primary"
          />
          <StatTile
            label="Quiz Ø"
            value={hasQuizAverage ? `${quizAveragePercent}%` : "–"}
            valueClassName={
              hasQuizAverage ? "text-primary" : "text-muted-foreground"
            }
          />
          <StatTile
            label="Gruppen-Ø"
            value={`${groupAveragePercent ?? 0}%`}
            valueClassName="text-primary"
          />
        </div>
        <Button
          type="button"
          className="w-full"
          onClick={openCourseAtLearnerPosition}
        >
          An der Position des Lernenden einsteigen
        </Button>
      </div>
      <CardContent
        ref={scrollContainerRef}
        className="flex flex-col gap-4 px-4 lg:flex-1 lg:overflow-y-auto lg:min-h-0 lg:[scrollbar-width:none] lg:[-ms-overflow-style:none] lg:[&::-webkit-scrollbar]:hidden"
      >
        {modules.length > 0 && (
          <div className="flex flex-col gap-2">
            <SectionHeading>Modul-Fortschritt</SectionHeading>
            <div className="flex flex-col gap-1">
              {modules.map((mod) => (
                <div
                  key={mod.title}
                  ref={
                    mod.title === currentModuleTitle
                      ? currentModuleRef
                      : undefined
                  }
                >
                  <ModuleRow module={mod} />
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
